import { useCallback, useLayoutEffect, useRef, useState } from "react";

// D-003 — the single portal/collision positioning mechanism, shared by
// CustomDropdown and LanguageSelector. A trigger passes its ref; the panel is
// rendered in a `document.body` portal with `position: fixed`, so no ancestor
// `overflow` (a horizontal filter bar, a modal, a sidebar) can ever clip it.
// Position is derived from the trigger's live bounding rect and, before paint,
// flipped above when there is no room below and clamped to the viewport
// horizontally. Modelled on AgentTenders' PitchPopover (getBoundingClientRect +
// flip/clamp), generalised so both dropdowns reuse the exact same maths.
//
// Options:
//   align            "left" | "right" — which trigger edge the panel aligns to
//   gap              px gap between the trigger and the panel (default 4)
//   matchTriggerWidth  panel width follows the trigger's width (menus that look
//                    like a <select>); otherwise the panel keeps its own width
//   maxWidth         optional hard cap for the panel width (px)
//   margin           minimum viewport inset kept when clamping (default 8)
//
// Returns { panelRef, style } — spread `style` onto the portalled panel and
// attach `panelRef` to it (used for measuring and for outside-click checks).
export default function useAnchoredPosition(
  isOpen,
  triggerRef,
  { align = "left", gap = 4, matchTriggerWidth = false, maxWidth, margin = 8 } = {},
) {
  const panelRef = useRef(null);
  const [style, setStyle] = useState(null);

  const compute = useCallback(() => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) return;

    const rect = trigger.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;

    // Width: either the trigger's width (select-style menus) or the panel's own
    // measured width, optionally capped so it never exceeds the viewport.
    let width = matchTriggerWidth ? rect.width : panel.offsetWidth;
    const hardCap = Math.min(maxWidth || Infinity, vw - margin * 2);
    if (width > hardCap) width = hardCap;

    // Horizontal — anchor to the chosen edge, then clamp inside the viewport.
    let left = align === "right" ? rect.right - width : rect.left;
    const maxLeft = vw - margin - width;
    if (left > maxLeft) left = maxLeft;
    if (left < margin) left = margin;

    // Vertical — below the trigger by default; flip above when the panel would
    // overflow the bottom edge and there is more room above.
    const panelH = panel.offsetHeight;
    let top = rect.bottom + gap;
    if (panelH && top + panelH > vh - margin) {
      const above = rect.top - gap - panelH;
      top = above >= margin ? above : Math.max(margin, vh - margin - panelH);
    }

    setStyle({
      position: "fixed",
      top: Math.round(top),
      left: Math.round(left),
      width: matchTriggerWidth ? Math.round(width) : undefined,
      maxWidth: maxWidth ? Math.round(hardCap) : `calc(100vw - ${margin * 2}px)`,
    });
  }, [triggerRef, align, gap, matchTriggerWidth, maxWidth, margin]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setStyle(null);
      return undefined;
    }
    // Runs after the portal has committed to the DOM but before paint, so the
    // first measured position is the one the user sees — no origin flash.
    compute();
    window.addEventListener("resize", compute);
    // Capture-phase so scrolling of ANY ancestor (modal body, page) re-anchors.
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [isOpen, compute]);

  return { panelRef, style };
}
